/**
 * Drives the Assistant panel's draggable, persisted width. The `resizing` flag
 * lets the caller suppress the slide transition mid-drag.
 */
import * as React from 'react';

// Bounded so the panel can't shrink past usefulness or swallow the whole screen.
const WIDTH_STORAGE_KEY = 'bravo-assistant-width';
const MIN_PANEL_WIDTH = 360;
const DEFAULT_PANEL_WIDTH = 820;

export function usePanelResize() {
  const [width, setWidth] = React.useState(readStoredPanelWidth);
  const [resizing, setResizing] = React.useState(false);

  React.useEffect(() => {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(Math.round(width)));
  }, [width]);

  // Drag the left edge: the panel is anchored right, so its width is the distance
  // from the cursor to the right edge. Tracked on `window` so a fast drag that
  // outruns the handle keeps resizing until the button is released.
  const startResize = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setResizing(true);
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => setWidth(clampPanelWidth(window.innerWidth - ev.clientX));
    const onUp = () => {
      setResizing(false);
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  return { width, resizing, startResize };
}

function clampPanelWidth(px: number): number {
  const max = Math.max(MIN_PANEL_WIDTH, Math.round(window.innerWidth * 0.9));
  return Math.min(Math.max(px, MIN_PANEL_WIDTH), max);
}

/** Stored width, else the responsive default. */
function readStoredPanelWidth(): number {
  const stored = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
  const initial =
    Number.isFinite(stored) && stored > 0
      ? stored
      : Math.min(window.innerWidth * 0.64, DEFAULT_PANEL_WIDTH);
  return clampPanelWidth(initial);
}
